import {
  CheckCircledIcon,
  Cross2Icon,
  CrossCircledIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import * as Toast from '@radix-ui/react-toast';
import { Box, Button, Flex, Heading, IconButton, Text } from '@radix-ui/themes';
import { IntercomArticleButton } from 'components/common/buttons/intercom-article';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { t } from 'i18next';
import { INotification, INotificationButton } from 'interfaces/i-notification';
import { ErrorLevel } from 'lib_ts/enums/errors.enums';
import {
  RADIX,
  RadixBtnSize,
  RadixBtnVariant,
  RadixColor,
} from 'lib_ts/enums/radix-ui';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './index.scss';

const ENABLE_TOAST_HEADERS = false;

const BTN_VARIANT: RadixBtnVariant = 'soft';
const BTN_SIZE: RadixBtnSize = RADIX.BUTTON.SIZE.XS;

interface IProps {
  config: INotification;

  // for inbox
  onDismiss?: () => void;
  // for inbox
  withoutToast?: boolean;
}

const ToastIcon = (props: { level?: ErrorLevel }) => {
  switch (props.level) {
    case ErrorLevel.error: {
      return <CrossCircledIcon />;
    }

    case ErrorLevel.warning: {
      return <ExclamationTriangleIcon />;
    }

    case ErrorLevel.success: {
      return <CheckCircledIcon />;
    }

    case ErrorLevel.debug:
    case ErrorLevel.info:
    default: {
      return <InfoCircledIcon />;
    }
  }
};

const ToastHeader = (props: IProps) => {
  const label = t(props.config.header ?? 'common.notification');

  return (
    <Heading
      size={RADIX.HEADING.SIZE.SM}
      className="hover-wrapper text-titlecase"
    >
      <span className="hover-show">
        {props.config.created
          ? formatDistanceToNow(props.config.created, {
              includeSeconds: true,
              addSuffix: true,
            })
          : label}
      </span>
      <span className="hover-hide">{label}</span>
      {props.config.isSuperAdminOnly && <SuperAdminIcon />}
    </Heading>
  );
};

const ToastButton = (props: {
  btn: INotificationButton;
  defaultColor: RadixColor | undefined;
  onClose: () => void;
}) => {
  return (
    <Box>
      <Button
        size={BTN_SIZE}
        variant={BTN_VARIANT}
        color={props.btn.color ?? props.defaultColor}
        onClick={() => {
          props.btn.onClick();

          if (props.btn.dismissAfterClick) {
            props.onClose();
          }
        }}
      >
        {t(props.btn.label)}
      </Button>
    </Box>
  );
};

export const CommonToast = (props: IProps) => {
  const [show, setShow] = useState(true);

  const closeToast = () => {
    if (props.config.onClose) {
      props.config.onClose();
    }

    // in inbox mode there is no toast lifecycle: dismissing means removing the
    // entry from the inbox store, so delegate to the provided handler
    if (props.withoutToast) {
      props.onDismiss?.();
      return;
    }

    // mark the notification as closed to prevent re-render
    props.config.closed = true;

    /** visibly hides the notification */
    setShow(false);
  };

  // in inbox mode the inbox injects its own working dismiss button, so drop any
  // caller-provided dismiss buttons to avoid rendering duplicates
  const footerButtons = useMemo(
    () =>
      (props.config.buttons ?? []).filter(
        (b) => !(props.withoutToast && b.kind === 'dismiss')
      ),
    [props.config.buttons, props.withoutToast]
  );

  const hasFooterActions =
    (props.withoutToast && props.onDismiss) ||
    footerButtons.length > 0 ||
    props.config.show_restart ||
    props.config.intercom_article_id;

  const icon = useMemo(() => {
    if (props.config.icon) {
      return props.config.icon;
    }

    if (props.config.isSuperAdminOnly) {
      return <SuperAdminIcon />;
    }

    return <ToastIcon level={props.config.level} />;
  }, [props.config]);

  const content = (
    <CommonCallout
      icon={<Box mt="1">{icon}</Box>}
      color={props.config.color}
      variant="outline"
      flexAlign="start"
      content={
        <Box className="ToastWrapper" mr="5">
          {ENABLE_TOAST_HEADERS && !props.config.hideHeader && (
            <ToastHeader {...props} />
          )}

          {/* content */}
          <Text style={{ color: RADIX.COLOR.SAFE_TEXT }}>
            <ReactMarkdown
              children={props.config.message_md ?? 'Empty notification body.'}
            />
          </Text>

          {hasFooterActions && (
            <Flex gap={RADIX.FLEX.GAP.SM} pt="4">
              {props.withoutToast && props.onDismiss && (
                <ToastButton
                  btn={{
                    label: 'common.dismiss',
                    kind: 'dismiss',
                    onClick: props.onDismiss,
                  }}
                  defaultColor={props.config.color}
                  onClose={() => {
                    // do nothing
                  }}
                />
              )}

              {footerButtons.map((b, i) => (
                <ToastButton
                  key={i}
                  btn={b}
                  defaultColor={props.config.color}
                  onClose={closeToast}
                />
              ))}

              {props.config.intercom_article_id && (
                <Box>
                  <IntercomArticleButton
                    variant={BTN_VARIANT}
                    size={BTN_SIZE}
                    articleID={props.config.intercom_article_id}
                    onClick={() => closeToast()}
                  />
                </Box>
              )}
            </Flex>
          )}
        </Box>
      }
    />
  );

  if (props.withoutToast) {
    return content;
  }

  return (
    <Toast.Root
      style={{ position: 'relative' }}
      className="ToastRoot"
      duration={props.config.delay_ms}
      open={show}
      onOpenChange={(open) => {
        if (!open) {
          closeToast();
        }
      }}
    >
      {content}

      <IconButton
        color={RADIX.COLOR.NEUTRAL}
        variant="soft"
        className="btn-floating"
        style={{
          position: 'absolute',
          top: 'var(--space-2)',
          right: 'var(--space-2)',
        }}
        onClick={() => closeToast()}
      >
        <Cross2Icon />
      </IconButton>
    </Toast.Root>
  );
};
